import { useEffect, useRef } from 'react';

// Keep keyboard focus and scrolling inside the sheet, including with the iOS keyboard open.
export function useModal(active: boolean, onClose: () => void) {
  const ref = useRef<HTMLElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!active || !ref.current) return;
    const modal = ref.current;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const scrollY = window.scrollY;
    const bodyStyle = document.body.style.cssText;
    Object.assign(document.body.style, { position: 'fixed', top: `-${scrollY}px`, width: '100%', overflow: 'hidden' });
    const shell = document.querySelector<HTMLElement>('.app-shell');
    const background = shell ? [...shell.children].filter((child): child is HTMLElement => child instanceof HTMLElement && !child.contains(modal)) : [];
    const inert = background.map(element => element.inert);
    background.forEach(element => { element.inert = true; });
    const focusable = () => [...modal.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled):not([type="hidden"]), select:not(:disabled), a[href], summary, [tabindex="0"]')].filter(element => element.tabIndex >= 0 && element.getClientRects().length > 0);
    const frame = requestAnimationFrame(() => modal.querySelector<HTMLInputElement>('input[aria-label="Amount"]')?.focus({ preventScroll: true }));
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); close.current(); }
      if (event.key !== 'Tab') return;
      const elements = focusable();
      const first = elements[0]; const last = elements.at(-1);
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && (document.activeElement === first || !modal.contains(document.activeElement))) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !modal.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
    };
    const viewport = window.visualViewport;
    const resize = () => {
      const backdrop = modal.parentElement!;
      backdrop.style.height = `${viewport?.height ?? window.innerHeight}px`;
      backdrop.style.top = `${viewport?.offsetTop ?? 0}px`;
    };
    resize();
    viewport?.addEventListener('resize', resize);
    viewport?.addEventListener('scroll', resize);
    document.addEventListener('keydown', keydown);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', keydown);
      viewport?.removeEventListener('resize', resize);
      viewport?.removeEventListener('scroll', resize);
      background.forEach((element, index) => { element.inert = inert[index]; });
      document.body.style.cssText = bodyStyle;
      window.scrollTo({ top: scrollY, behavior: 'instant' });
      previousFocus?.focus({ preventScroll: true });
    };
  }, [active]);
  return ref;
}
